import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { useFpB2bShippingService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-shipping-service'
import { LoaderFullPage } from '@cart-ui/LoaderFullPage/LoaderFullPage'
import { useApiCampaignMember } from '@hooks-api/fortune/useApiCampaignMember'
import { type CheckoutOrder } from '@hooks-api/types/dataModel/orders'
import { SHIPPING_METHOD_TYPE } from '@hooks-api/types/dataModel/shipping'
import { Checkbox } from '@pets-ui/Checkbox/Checkbox'
import { HStack } from '@pets-ui/HStack/HStack'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { ThemeProvider } from '@pets-ui/ThemeProvider/ThemeProvider'
import { colorTheme as fastpassColorTheme } from '@pets-ui/themes/fastpass'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useCheckout } from '../../../../fp-b2b-shared/hooks/use-checkout'

const styles = stylex.create({
  root: {
    padding: 20,
  },
  checkbox: {
    color: colors.grey900,

  },
})

export function OrderNotes() {
  const { watch, control, setValue } = useFormContext<CheckoutOrder>()
  const formValues = watch()
  const isPostingDateInNextMonth = !!formValues?.posting_date_in_next_month
  const isNeedPdf = !!formValues?.need_order_pdf
  const { campaignId, isInRetryPaymentPage, platform } = useCheckout()
  const { campaignCodeFromUrl } = useCampaignCode()
  const { destructuredData: member, isLoading: isLoadingMember } = useApiCampaignMember({ code: campaignCodeFromUrl })
  const { appliedShippingMethod } = useFpB2bShippingService(campaignId, campaignCodeFromUrl, platform)
  const isCurrentUserAuthorized = member?.roles.some(role =>
    ['shop_manager', 'mbpartner', 'mbpartner_invoice'].includes(role),
  )
  const isPallet = useMemo(() => appliedShippingMethod?.shipping_method_type === SHIPPING_METHOD_TYPE.PALLET, [appliedShippingMethod?.shipping_method_type])

  useEffect(() => {
    if (isPallet) {
      setValue('is_priority_shipping', false)
    }
  }, [isPallet, setValue])

  const isCurrentUserRoleShopManager = member?.roles.includes('shop_manager')
  if (!isCurrentUserRoleShopManager) return null // NOTE: 僅shop manager能操作

  return (
    <>
      {isLoadingMember && <LoaderFullPage />}
      <ThemeProvider theme={fastpassColorTheme}>
        <VStack gap={2}>
          <Paper elevation={0} borderRadius={8} rootStyle={styles.root}>
            <VStack gap={3} alignItems="flex-start">
              <Text variant="subtitle" color={colors.grey900}>
                訂單備註
              </Text>
              {isCurrentUserAuthorized && (
                <HStack gap={2} alignItems="center">
                  <Controller
                    control={control}
                    name="posting_date_in_next_month"
                    render={({ field }) => (
                      <Checkbox
                        onChange={field.onChange}
                        label={<Text rootStyle={styles.checkbox}>跨月帳款</Text>}
                        checked={isPostingDateInNextMonth}
                        disabled={isInRetryPaymentPage}
                      />
                    )}
                  />
                  <Text variant="caption" color={colors.grey500}>
                    帳款自動歸入次月的 1 號做計算
                  </Text>
                </HStack>
              )}
              <Controller
                control={control}
                name="is_priority_shipping"
                render={({ field }) => (
                  <Checkbox
                    onChange={field.onChange}
                    label={<Text rootStyle={styles.checkbox}>優先出貨</Text>}
                    checked={!!formValues?.is_priority_shipping}
                    disabled={isInRetryPaymentPage || isPallet}
                  />
                )}
              />
              <Controller
                control={control}
                name="need_order_pdf"
                render={({ field }) => (
                  <Checkbox
                    onChange={field.onChange}
                    label={<Text rootStyle={styles.checkbox}>需附訂單 Invoice</Text>}
                    checked={isNeedPdf}
                    disabled={isInRetryPaymentPage}
                  />
                )}
              />
              <Controller
                control={control}
                name="need_purchase_order"
                render={({ field }) => (
                  <Checkbox
                    onChange={field.onChange}
                    label={<Text rootStyle={styles.checkbox}>需附上採購單</Text>}
                    checked={!!formValues?.need_purchase_order}
                    disabled={isInRetryPaymentPage}
                  />
                )}
              />
              <Controller
                control={control}
                name="is_specific_process"
                render={({ field }) => (
                  <Checkbox
                    onChange={field.onChange}
                    label={<Text rootStyle={styles.checkbox}>特殊加工</Text>}
                    checked={!!formValues?.is_specific_process}
                    disabled={isInRetryPaymentPage}
                  />
                )}
              />

            </VStack>
          </Paper>
        </VStack>
      </ThemeProvider>
    </>
  )
}
